import * as THREE from 'three/webgpu';
import type { FeatureFace } from '../../mesh/feature';
import { FEATURE_FACE_BOUNDARY_VERTEX_SAMPLE_MAX } from '../../mesh/feature/acceleration/featureFaceSpatialQueryTypes';

/**
 * 读取面代理边界上的唯一世界坐标顶点。
 *
 * 设计说明：
 * 1. 有界面最近点常出现在边界，BVH 全局初解后仍需少量边界采样兜底；
 * 2. 通过索引边出现次数识别边界边，避免全顶点 O(V) 采样；
 * 3. 超出上限时均匀抽稀，控制测量热路径成本。
 *
 * @param face 面代理。
 * @param maxVertices 最大采样顶点数。
 * @returns 边界世界坐标顶点列表。
 */
export function collectFeatureFaceBoundaryWorldVertices(
    face: FeatureFace,
    maxVertices: number = FEATURE_FACE_BOUNDARY_VERTEX_SAMPLE_MAX,
): THREE.Vector3[] {
    const positionAttribute = face.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
        return [];
    }

    face.updateWorldMatrix(true, false);
    const indexAttribute = face.geometry.getIndex();
    const edgeUsage = new Map<string, number>();

    const registerEdge = (firstIndex: number, secondIndex: number): void => {
        const edgeKey = firstIndex < secondIndex
            ? `${firstIndex}:${secondIndex}`
            : `${secondIndex}:${firstIndex}`;
        edgeUsage.set(edgeKey, (edgeUsage.get(edgeKey) ?? 0) + 1);
    };

    const triangleCount = indexAttribute instanceof THREE.BufferAttribute
        ? indexAttribute.count / 3
        : positionAttribute.count / 3;

    for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
        const cursor = triangleIndex * 3;
        const indexA = indexAttribute instanceof THREE.BufferAttribute
            ? Number(indexAttribute.getX(cursor))
            : cursor;
        const indexB = indexAttribute instanceof THREE.BufferAttribute
            ? Number(indexAttribute.getX(cursor + 1))
            : cursor + 1;
        const indexC = indexAttribute instanceof THREE.BufferAttribute
            ? Number(indexAttribute.getX(cursor + 2))
            : cursor + 2;
        registerEdge(indexA, indexB);
        registerEdge(indexB, indexC);
        registerEdge(indexC, indexA);
    }

    const boundaryVertexIndices = new Set<number>();
    edgeUsage.forEach((usageCount, edgeKey) => {
        if (usageCount !== 1) {
            return;
        }
        const [firstIndexRaw, secondIndexRaw] = edgeKey.split(':');
        boundaryVertexIndices.add(Number(firstIndexRaw));
        boundaryVertexIndices.add(Number(secondIndexRaw));
    });

    const uniqueKeys = new Set<string>();
    const vertices: THREE.Vector3[] = [];
    const scratch = new THREE.Vector3();
    const sortedIndices = Array.from(boundaryVertexIndices.values()).sort((left, right) => left - right);
    const stride = sortedIndices.length <= maxVertices
        ? 1
        : Math.ceil(sortedIndices.length / maxVertices);

    for (let cursor = 0; cursor < sortedIndices.length; cursor += stride) {
        const vertexIndex = sortedIndices[cursor]!;
        scratch.set(
            positionAttribute.getX(vertexIndex),
            positionAttribute.getY(vertexIndex),
            positionAttribute.getZ(vertexIndex),
        );
        scratch.applyMatrix4(face.matrixWorld);
        const key = `${scratch.x.toFixed(6)}:${scratch.y.toFixed(6)}:${scratch.z.toFixed(6)}`;
        if (uniqueKeys.has(key)) {
            continue;
        }
        uniqueKeys.add(key);
        vertices.push(scratch.clone());
    }

    return vertices;
}
